import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    type ElementRef,
    type OnInit,
    computed,
    effect,
    inject,
    input,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { NgIcon } from '@ng-icons/core';
import {
    isResolvedProblemDetails,
    qpxToast as toast,
} from '@quipex/core/utils';
import {
    DialogContent,
    DialogFooter,
    DialogHeader,
    Loader,
    QpxButton,
    QpxFormFieldComponents,
    QpxFormFieldDirectives,
    QpxIcon,
    QpxOverflow,
    QpxTypography,
    QpxTooltip,
} from '@quipex/shared/ui';
import {
    EMPTY,
    Subject,
    catchError,
    map,
    merge,
    of,
    switchMap,
    tap,
    timer,
} from 'rxjs';
import { FormState } from './form-state';
import { ServerState } from './server-state';

type RecipientPreviewState =
    | 'idle'
    | 'loading'
    | 'recognised'
    | 'unknown'
    | 'unavailable'
    | 'failure';

const RECIPIENT_PREVIEW_DEBOUNCE_MS = 400;

@Component({
    selector: 'qpx-transfer-building-ownership-dialog',
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatInputModule,
        MatCheckboxModule,
        QpxTooltip,
        NgIcon,
        DialogHeader,
        Loader,
        QpxButton,
        DialogContent,
        DialogFooter,
        QpxFormFieldComponents,
        QpxFormFieldDirectives,
        QpxIcon,
        QpxOverflow,
        QpxTypography,
    ],
    templateUrl: './transfer-building-ownership-dialog.html',
    styles: `
        .icon-container {
            border-radius: var(--qpx-radius-sm);
            background-color: oklch(
                from var(--color-destructive-muted) calc(l * 0.95)
                    calc(c * 1.75) h
            );
        }
    `,
    providers: [FormState, ServerState],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferBuildingOwnershipDialog implements OnInit {
    //#region Dependency injection
    private readonly dialogRef =
        inject<MatDialogRef<TransferBuildingOwnershipDialog, boolean>>(
            MatDialogRef
        );
    private readonly destroyRef = inject(DestroyRef);
    private readonly formState = inject(FormState);
    private readonly serverState = inject(ServerState);
    //#endregion

    //#region Inputs
    public readonly buildingId = input.required<string>();
    public readonly buildingName = input.required<string>();
    private readonly emailInput =
        viewChild.required<ElementRef<HTMLInputElement>>('emailInput');
    //#endregion

    //#region Form state
    protected readonly id = this.formState.id;
    protected readonly form = this.formState.form;
    protected readonly recipientEmailCtrl = this.formState.recipientEmailCtrl;
    protected readonly isProcessing = this.serverState.isProcessing;
    //#endregion

    //#region State
    protected readonly recipientPreview = this.serverState.recipientPreview;
    protected readonly recipientPreviewState = computed<RecipientPreviewState>(
        () => {
            if (this.serverState.isCheckingRecipient()) return 'loading';
            if (this.serverState.hasRecipientPreviewError()) return 'failure';

            const preview = this.recipientPreview();
            if (!preview) return 'idle';
            if (preview.state === 'failure') return 'failure';
            if (
                preview.state === 'recognised' &&
                preview.organisationName?.trim()
            ) {
                return 'recognised';
            }
            if (preview.state === 'unknown') return 'unknown';

            return 'unavailable';
        }
    );
    private readonly retryRecipientPreview$ = new Subject<void>();
    //#endregion

    //#region Lifecycle
    constructor() {
        effect(() => {
            const retentionControl =
                this.form().controls.retainOriginalOwnerAccess;
            if (this.isProcessing()) {
                retentionControl.disable({ emitEvent: false });
            } else {
                retentionControl.enable({ emitEvent: false });
            }
        });
    }

    public ngOnInit(): void {
        this.setupRecipientPreviewStream();
    }
    //#endregion

    //#region Template methods
    protected onCancel(): void {
        if (this.isProcessing()) {
            return;
        }

        this.dialogRef.close(false);
    }

    protected onConfirm(): void {
        if (this.isProcessing() || this.serverState.isCheckingRecipient()) {
            return;
        }

        this.formState
            .validateAndBuildPayload()
            .pipe(
                switchMap((payload) =>
                    this.serverState.startTransfer(this.buildingId(), payload)
                ),
                tap({
                    next: () => {
                        toast.success('Transfer request sent');
                        this.dialogRef.close(true);
                    },
                }),
                catchError((error: unknown) => {
                    toast.error(toTransferErrorMessage(error));
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected retryRecipientPreview(): void {
        this.retryRecipientPreview$.next();
        this.emailInput().nativeElement.focus();
    }
    //#endregion

    //#region Private methods
    private setupRecipientPreviewStream(): void {
        const emailCtrl = this.recipientEmailCtrl();
        const emailChanges$ = emailCtrl.valueChanges.pipe(
            map((email) => ({ email, debounce: true })),
            tap(({ email }) => {
                this.serverState.resetRecipientPreview();
                if (emailCtrl.valid && email.trim()) {
                    this.serverState.startRecipientPreview();
                }
            })
        );
        const retry$ = this.retryRecipientPreview$.pipe(
            map(() => ({ email: emailCtrl.value, debounce: false }))
        );

        merge(emailChanges$, retry$)
            .pipe(
                switchMap(({ email, debounce }) => {
                    const normalizedEmail = email.trim();
                    if (!emailCtrl.valid || !normalizedEmail) return EMPTY;

                    const delay$ = debounce
                        ? timer(RECIPIENT_PREVIEW_DEBOUNCE_MS)
                        : of(0);
                    return delay$.pipe(
                        switchMap(() =>
                            this.serverState.checkRecipientEmail(
                                this.buildingId(),
                                normalizedEmail
                            )
                        )
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }
    //#endregion
}

function toTransferErrorMessage(error: unknown): string {
    if (!isResolvedProblemDetails(error)) {
        return 'Failed to start transfer';
    }

    return error.detail ?? error.title;
}
